import React from 'react';
import { motion } from 'framer-motion';
import { Home, HelpCircle } from 'lucide-react';
import { useWirelessAdvisor } from '../../context/WirelessAdvisorContext';
import { WIRELESS_SPECS } from '../../utils/constants';
import QuestionTooltip from '../shared/QuestionTooltip';
import { trackEvent } from '../../../../utils/analytics';

/**
 * HomeSize component
 * First step in the wireless advisor form
 * Allows users to select their home size
 */
const HomeSize = () => {
  const { formData, updateFormData } = useWirelessAdvisor();
  
  // Handle home size selection
  const handleSizeSelect = (size, details) => {
    // Update form data
    updateFormData({
      homeSize: size,
      area: details.max,
      minArea: details.min,
      maxArea: details.max
    });
    
    // Track event
    trackEvent('wireless_advisor_home_size_selected', {
      size,
      area: details.max,
      min_area: details.min,
      max_area: details.max
    });
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">What size is your home?</h2>
        <QuestionTooltip 
          content="Select the option that best matches your home's floor area. This helps us determine the optimal wireless setup for your space."
          iconSize="md"
        />
      </div>
      
      <p className="text-gray-400 text-sm">
        Choose the approximate size of your home to help us recommend the right wireless setup.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(WIRELESS_SPECS.HOME_SIZES).map(([size, details]) => (
          <motion.button
            key={size}
            onClick={() => handleSizeSelect(size, details)}
            className={`p-6 rounded-xl border-2 transition-all ${
              formData.homeSize === size
                ? 'border-purple-600 bg-purple-600/10'
                : 'border-gray-700 hover:border-purple-500/50 bg-gray-800/50'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-start">
              <div className={`p-2 rounded-lg ${formData.homeSize === size ? 'bg-purple-600/20' : 'bg-gray-700'} mr-4`}>
                <Home className={`h-6 w-6 ${formData.homeSize === size ? 'text-purple-400' : 'text-gray-400'}`} />
              </div>
              <div className="text-left">
                <span className={`font-medium ${formData.homeSize === size ? 'text-purple-400' : 'text-white'}`}>
                  {details.label}
                </span>
                <p className="text-sm text-gray-400 mt-1">
                  {details.min} - {details.max === 500 ? '500+' : details.max} square meters
                </p>
              </div>
            </div>
          </motion.button>
        ))}
      </div>
      
      {formData.homeSize && (
        <motion.div
          className="mt-6 p-4 bg-purple-600/10 border border-purple-600/20 rounded-lg"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="text-lg font-medium text-purple-400 mb-2">
            {WIRELESS_SPECS.HOME_SIZES[formData.homeSize].label} Home
          </h3>
          <p className="text-sm text-gray-300">
            Based on your selection, we'll recommend a wireless setup optimized for a home of approximately {formData.area} square meters.
          </p>
          {formData.homeSize === 'LARGE' || formData.homeSize === 'XLARGE' ? (
            <p className="text-sm text-gray-300 mt-2">
              For larger homes like yours, we may recommend multiple access points to ensure complete coverage.
            </p>
          ) : null}
        </motion.div>
      )}
    </div>
  );
};

export default HomeSize;